/**
 * Exponential Backoff Retry Service
 * 
 * Implements intelligent retry logic with exponential backoff for API calls
 * to prevent overwhelming the server and reduce unnecessary load.
 * 
 * Example:
 * const retryConfig = {
 *   initialDelayMs: 1000,
 *   maxDelayMs: 30000,
 *   maxAttempts: 5,
 *   backoffFactor: 2
 * };
 * this.retryService.getRetryDelay(currentAttempt, retryConfig)
 */

export interface ExponentialBackoffConfig {
  /** Initial delay in milliseconds (default: 1000) */
  initialDelayMs?: number;
  
  /** Maximum delay in milliseconds (default: 30000) */
  maxDelayMs?: number;
  
  /** Maximum number of retry attempts (default: 5) */
  maxAttempts?: number;
  
  /** Backoff multiplier for each attempt (default: 2) */
  backoffFactor?: number;
}

export class ExponentialBackoffRetryService {
  private static readonly DEFAULT_CONFIG: ExponentialBackoffConfig = {
    initialDelayMs: 1000,
    maxDelayMs: 30000,
    maxAttempts: 5,
    backoffFactor: 2
  };

  /**
   * Calculates the delay for the next retry attempt using exponential backoff
   * Formula: min(initialDelay * (backoffFactor ^ attempt), maxDelay)
   * 
   * @param currentAttempt - Current attempt number (0-indexed)
   * @param config - Retry configuration
   * @returns Delay in milliseconds for next retry
   */
  static getRetryDelay(
    currentAttempt: number,
    config: ExponentialBackoffConfig = {}
  ): number {
    const finalConfig = { ...this.DEFAULT_CONFIG, ...config };
    
    // Calculate exponential backoff: initialDelay * (backoffFactor ^ attempt)
    const exponentialDelay = finalConfig.initialDelayMs! * 
      Math.pow(finalConfig.backoffFactor!, currentAttempt);
    
    // Cap at maximum delay
    return Math.min(exponentialDelay, finalConfig.maxDelayMs!);
  }

  /**
   * Checks if max retry attempts have been reached
   * @param currentAttempt - Current attempt number (0-indexed)
   * @param config - Retry configuration
   * @returns true if max attempts exceeded
   */
  static shouldStopRetrying(
    currentAttempt: number,
    config: ExponentialBackoffConfig = {}
  ): boolean {
    const finalConfig = { ...this.DEFAULT_CONFIG, ...config };
    return currentAttempt >= finalConfig.maxAttempts!;
  }

  /**
   * Generates a readable description of retry strategy
   * Useful for logging and debugging
   */
  static getRetryStrategyDescription(config: ExponentialBackoffConfig = {}): string {
    const finalConfig = { ...this.DEFAULT_CONFIG, ...config };
    return `Exponential backoff: ${finalConfig.initialDelayMs}ms initial, ` +
           `${finalConfig.backoffFactor}x multiplier, ` +
           `max ${finalConfig.maxDelayMs}ms, ` +
           `${finalConfig.maxAttempts} attempts`;
  }
}

/**
 * Example Retry Strategy Presets
 */
export const RETRY_STRATEGIES = {
  /** Aggressive: Quick retries for temporary network hiccups */
  AGGRESSIVE: {
    initialDelayMs: 500,
    maxDelayMs: 10000,
    maxAttempts: 5,
    backoffFactor: 2
  } as ExponentialBackoffConfig,

  /** Default: Balanced for most API calls */
  DEFAULT: {
    initialDelayMs: 1000,
    maxDelayMs: 30000,
    maxAttempts: 5,
    backoffFactor: 2
  } as ExponentialBackoffConfig,

  /** Conservative: Longer waits for rate-limited APIs */
  CONSERVATIVE: {
    initialDelayMs: 2000,
    maxDelayMs: 60000,
    maxAttempts: 4,
    backoffFactor: 3
  } as ExponentialBackoffConfig,

  /** Long-polling: For background sync operations */
  LONG_POLLING: {
    initialDelayMs: 5000,
    maxDelayMs: 120000,
    maxAttempts: 10,
    backoffFactor: 1.5
  } as ExponentialBackoffConfig
};
